import {useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {DashboardLayout} from "../../layouts/DashboardLayout"
import {
    createLessonApi,
    uploadLessonFileApi,
    LessonType,
    LessonAccessType,
} from "../../features/lessons/api/lesson.api"

/* ===================== TYPES ===================== */

type LessonFile = {
    id: string
    url: string
    filename: string
    size: number
    kind?: string
}

/* ===================== UI ===================== */

const Card = ({children}: { children: React.ReactNode }) => (
    <div className="bg-white p-6 rounded-xl border space-y-4">
        {children}
    </div>
)

const Input = ({
                   label,
                   value,
                   onChange,
               }: {
    label: string
    value: string
    onChange: (v: string) => void
}) => (
    <div>
        <label className="block text-sm mb-1">{label}</label>
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
        />
    </div>
)

const Textarea = ({
                      label,
                      value,
                      onChange,
                  }: {
    label: string
    value: string
    onChange: (v: string) => void
}) => (
    <div>
        <label className="block text-sm mb-1">{label}</label>
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
            className="w-full border rounded-lg px-3 py-2"
        />
    </div>
)

const Select = <T extends string>({
                                      label,
                                      value,
                                      onChange,
                                      options,
                                  }: {
    label: string
    value: T
    onChange: (v: T) => void
    options: { value: T; label: string }[]
}) => (
    <div>
        <label className="block text-sm mb-1">{label}</label>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value as T)}
            className="w-full border rounded-lg px-3 py-2"
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    </div>
)

const Checkbox = ({
                      label,
                      checked,
                      onChange,
                  }: {
    label: string
    checked: boolean
    onChange: (v: boolean) => void
}) => (
    <label className="flex items-center gap-2 text-sm">
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
        />
        {label}
    </label>
)

/* ===================== ATTACHMENTS ===================== */

const AttachmentsBlock = ({
                              files,
                              setFiles,
                          }: {
    files: LessonFile[]
    setFiles: React.Dispatch<
        React.SetStateAction<LessonFile[]>
    >
}) => {
    const upload = async (file: File) => {
        const res = await uploadLessonFileApi(file)
        setFiles((prev) => [...prev, res.data])
    }

    return (
        <div className="space-y-2">
            <h3 className="font-semibold">Вложения</h3>

            <input
                type="file"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) upload(file)
                }}
            />

            {files.map((f) => (
                <div
                    key={f.id}
                    className="flex items-center justify-between text-sm"
                >
                    <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                    >
                        {f.filename}
                    </a>

                    <span className="text-gray-400 text-xs">
            {(f.size / 1024).toFixed(1)} КБ
          </span>
                </div>
            ))}
        </div>
    )
}

/* ===================== PAGE ===================== */

export const CreateLessonPage = () => {
    const {moduleId} = useParams<{ moduleId: string }>()
    const navigate = useNavigate()

    const [title, setTitle] = useState("")
    const [type, setType] = useState<LessonType>("text")
    const [accessType, setAccessType] =
        useState<LessonAccessType>("paid")
    const [content, setContent] = useState("")
    const [duration, setDuration] = useState("")
    const [files, setFiles] = useState<LessonFile[]>([])
    const [isActive, setIsActive] = useState(true)

    const submit = async () => {
        if (!moduleId) return
        if (!title.trim()) return

        await createLessonApi({
            module_id: moduleId,
            title,
            type,
            access_type: accessType,
            content: content || "",
            duration,
            file_ids: files.map((f) => f.id), // 🔑 ВАЖНО
            is_active: isActive,
        })

        navigate(-1)
    }

    return (
        <DashboardLayout title="Создание урока">
            <div className="grid grid-cols-3 gap-6">

                {/* LEFT */}
                <div className="col-span-2 space-y-6">
                    <Card>
                        <Input
                            label="Название"
                            value={title}
                            onChange={setTitle}
                        />

                        <Select
                            label="Тип урока"
                            value={type}
                            onChange={setType}
                            options={[
                                {value: "text", label: "Текст"},
                                {value: "video", label: "Видео"},
                            ]}
                        />

                        <Textarea
                            label={
                                type === "text"
                                    ? "Содержимое"
                                    : "Описание"
                            }
                            value={content}
                            onChange={setContent}
                        />

                        {type === "video" && (
                            <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5">
                                Видео можно будет загрузить после создания урока — на странице урока.
                            </p>
                        )}
                    </Card>

                    <Card>
                        <AttachmentsBlock
                            files={files}
                            setFiles={setFiles}
                        />
                    </Card>
                </div>

                {/* RIGHT */}
                <div className="space-y-6">
                    <Card>
                        <Select
                            label="Доступ"
                            value={accessType}
                            onChange={setAccessType}
                            options={[
                                {value: "free", label: "Бесплатно"},
                                {value: "paid", label: "Платно"},
                            ]}
                        />

                        <Input
                            label="Длительность (минут)"
                            value={duration}
                            onChange={(v) => {
                                if (/^\d*$/.test(v)) {
                                    setDuration(v)
                                }
                            }}
                        />

                        <Checkbox
                            label="Активен"
                            checked={isActive}
                            onChange={setIsActive}
                        />
                    </Card>

                    <button
                        onClick={submit}
                        className="bg-blue-600 text-white py-2 rounded-lg"
                    >
                        Создать урок
                    </button>
                </div>
            </div>
        </DashboardLayout>
    )
}
